import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicApiClient } from '@/api/public'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'
import { useEmbedResize } from '@/hooks/useEmbedResize'
import { CourseCard } from './PublicCourseParts'
import { cleanColour, embedAccentVars } from './brand'

/**
 * The course catalogue as it appears inside a customer's iframe: just the cards, no page chrome. The address can
 * narrow it: category=slug, courses=slug1,slug2, limit=n, and colour=#rrggbb for the accent (else the customer's own colour).
 */
export default function EmbedCoursesPage() {
  const { slug } = useParams<{ slug: string }>()
  const [params] = useSearchParams()
  const frame = useEmbedResize()

  const category = params.get('category')
  const wanted = params.get('courses')?.split(',').map(s => s.trim()).filter(Boolean) ?? []
  const limit = parseInt(params.get('limit') ?? '')
  const colour = cleanColour(params.get('colour') ?? params.get('primaryColour'))

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-courses', slug],
    queryFn: () => publicApiClient.getPublicCourseCatalogue(slug!).then(r => r.data),
    enabled: !!slug,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency)
  const { formatDate } = datesFor(data?.region, data?.timeZoneId)

  const courses = (data?.courses ?? [])
    .filter(c => !category || c.categorySlug === category)
    .filter(c => wanted.length === 0 || wanted.includes(c.slug))
    .slice(0, Number.isFinite(limit) && limit > 0 ? limit : undefined)

  return (
    <div ref={frame} className="flow-root p-1 font-sans" style={embedAccentVars(data ?? { primaryColour: null, secondaryColour: null }, colour)}>
      {isLoading && (
        <div className="flex justify-center p-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--brand-accent)] border-t-transparent" />
        </div>
      )}
      {!isLoading && (isError || !data) && <p className="p-8 text-center text-sm text-slate-400">Courses unavailable.</p>}
      {data && courses.length === 0 && (
        <div className="p-8 text-center">
          <p className="font-semibold text-slate-800">{data.tenantName}</p>
          <p className="mt-1 text-sm text-slate-400">No courses to show at the moment. Check back soon.</p>
        </div>
      )}
      {data && courses.length > 0 && (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4">
          {courses.map(course => (
            <li key={course.slug}>
              <CourseCard
                embed
                tenantSlug={slug!}
                course={course}
                category={data.categories.find(c => c.slug === course.categorySlug)}
                fmtMoney={fmtMoney}
                formatDate={formatDate}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
